import { Mercor__Bids__BidItemRowControllerIdentifier } from "controllers/identifiers";
import BaseController from "confinus_ui/base_controller";
import BidItemRowController from "./bid_item_row_controller";

export default class BidDetailController extends BaseController {
  static outlets = [Mercor__Bids__BidItemRowControllerIdentifier];
  static targets = ["totalDisplay", "footerTotal", "scrollContainer"];

  declare totalDisplayTarget: HTMLElement;
  declare footerTotalTarget: HTMLElement;
  declare hasTotalDisplayTarget: boolean;
  declare hasFooterTotalTarget: boolean;
  declare readonly scrollContainerTarget: HTMLDivElement;
  declare readonly hasScrollContainerTarget: boolean;

  // Outlet accessors - naming follows Stimulus convention: camelCase of the outlet identifier
  declare readonly mercorBidsBidItemRowOutlets: BidItemRowController[];
  private get itemRowControllers(): BidItemRowController[] {
    return this.mercorBidsBidItemRowOutlets;
  }

  static values = {
    maxDeviation: { type: Number, default: 0 },
  };

  declare maxDeviationValue: number;

  private boundRecalculateTotal = this.recalculateTotal.bind(this);
  private resizeObserver: ResizeObserver | null = null;

  connect() {
    super.connect();
    this.recalculateTotal();
    this.element.addEventListener(
      "mercor--bids--bid-item-row:price-change",
      this.boundRecalculateTotal,
    );

    if (this.hasScrollContainerTarget) {
      this.contentScrolled();
      this.resizeObserver = new ResizeObserver(() => this.contentScrolled());
      this.resizeObserver.observe(this.scrollContainerTarget);
    }
  }

  disconnect() {
    this.element.removeEventListener(
      "mercor--bids--bid-item-row:price-change",
      this.boundRecalculateTotal,
    );
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    super.disconnect();
  }

  contentScrolled() {
    if (!this.hasScrollContainerTarget) return;

    const scrollContainer = this.scrollContainerTarget;
    const shadowTarget = scrollContainer.parentElement;
    if (!shadowTarget) return;

    const x = scrollContainer.scrollLeft;
    const divWidth = scrollContainer.scrollWidth - scrollContainer.clientWidth;

    if (x == 0) {
      shadowTarget.classList.remove("inset-scroll-shadow-not-at-left");
    }
    if (x > 0) {
      shadowTarget.classList.add("inset-scroll-shadow-not-at-left");
    }
    if (x < divWidth) {
      shadowTarget.classList.add("inset-scroll-shadow-not-at-right");
    }
    if (x == divWidth) {
      shadowTarget.classList.remove("inset-scroll-shadow-not-at-right");
    }
  }

  recalculateTotal() {
    let total = 0;

    this.itemRowControllers.forEach((controller) => {
      if (controller.hasQuantityTarget && controller.hasProposedPriceTarget) {
        const qty = parseFloat(controller.quantityTarget.value) || 0;
        const price = parseFloat(controller.proposedPriceTarget.value) || 0;
        total += qty * price * 100;
      }
    });

    const formatted = this.formatCurrency(total);
    if (this.hasTotalDisplayTarget) {
      this.totalDisplayTarget.textContent = formatted;
    }
    if (this.hasFooterTotalTarget) {
      this.footerTotalTarget.textContent = formatted;
    }
  }

  private formatCurrency(cents: number): string {
    const dollars = cents / 100;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(dollars);
  }
}
